import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { loadBlogPost } from "~/utils/content-fns";
import { seo, seoLinks, articleJsonLd, breadcrumbJsonLd } from "~/utils/seo";
import { BlogNewsletterSidebar } from "~/components/BlogNewsletterSidebar";
import { TableOfContents } from "~/components/TableOfContents";
import { AskAiSummary } from "~/components/AskAiSummary";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { AuthorAvatar } from "~/components/AuthorAvatar";
import { ImageZoom } from "~/components/ImageZoom";
import { BlogAnimationEmbeds } from "~/components/BlogAnimationEmbeds";
import { BlogSceneEmbeds } from "~/components/BlogSceneEmbeds";
import { mountC1BridgeDiagram } from "~/components/blocks/C1BridgeDiagram";
import { TEAM_AUTHOR_KEY, authorPath } from "~/utils/authors";
import { resolveSiteUrl } from "~/utils/resolve-site-url";
import type { AuthorView, RelatedPost } from "~/types/views";

/**
 * Named people become schema.org Person authors. The collective C1 byline is
 * left out so `articleJsonLd` falls back to crediting the Organization, which
 * is what "The C1 Team" actually means. Mirrors `guides/$slug.tsx`.
 */
function personAuthors(authors: AuthorView[] | undefined) {
  const people = (authors ?? []).filter((a) => a.key !== TEAM_AUTHOR_KEY);
  return people.length > 0
    ? people.map((a) => ({
        name: a.name,
        url: a.hasPage ? authorPath(a.key) : null,
      }))
    : undefined;
}

/**
 * The post's place in the site hierarchy, shared by the visible trail and the
 * BreadcrumbList schema so the two can't drift. The current page carries no
 * URL — see `breadcrumbJsonLd`.
 */
function blogCrumbs(title: string) {
  return [
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" },
    { name: title },
  ];
}

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => loadBlogPost({ data: { slug: params.slug } }),
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.seo_title || loaderData.title,
          description: loaderData.seo_description || loaderData.description,
          image: loaderData.featured_image,
          url: `/blog/${loaderData.slug}`,
          noindex: loaderData.robots === "noindex",
          type: "article",
          publishedTime: loaderData.date,
          modifiedTime: loaderData.last_updated,
          authors: loaderData.authors,
          section: loaderData.categories[0] ?? null,
        })
      : [],
    links: loaderData ? seoLinks({ url: `/blog/${loaderData.slug}` }) : [],
    scripts:
      loaderData && loaderData.robots !== "noindex"
        ? [
            articleJsonLd({
              type: "BlogPosting",
              url: `/blog/${loaderData.slug}`,
              title: loaderData.title,
              description: loaderData.description,
              image: loaderData.featured_image,
              datePublished: loaderData.date,
              dateModified: loaderData.last_updated,
              authors: personAuthors(loaderData.authors),
              articleSection: loaderData.categories,
              keywords: loaderData.categories,
            }),
            breadcrumbJsonLd(blogCrumbs(loaderData.title)),
          ].filter((s) => s !== null)
        : [],
  }),
  component: BlogPost,
});

/* ------------------------------------------------------------------ */
/*  LinkedIn share badge SVG (matches Hugo exactly)                    */
/* ------------------------------------------------------------------ */
function LinkedInBadge() {
  return (
    <svg className="h-auto w-9" width="37" height="30" viewBox="0 0 37 30" fill="none">
      <rect y="0.5" width="37" height="29" rx="6" fill="white" />
      <rect y="0.5" width="37" height="29" rx="6" fill="#E6F5F0" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M27 23.5H23.507V17.5507C23.507 15.9196 22.8872 15.008 21.5962 15.008C20.1917 15.008 19.4579 15.9566 19.4579 17.5507V23.5H16.0917V12.1667H19.4579V13.6933C19.4579 13.6933 20.4701 11.8204 22.8751 11.8204C25.279 11.8204 27 13.2884 27 16.3244V23.5ZM12.0757 10.6827C10.9291 10.6827 10 9.74623 10 8.59133C10 7.43642 10.9291 6.5 12.0757 6.5C13.2224 6.5 14.1509 7.43642 14.1509 8.59133C14.1509 9.74623 13.2224 10.6827 12.0757 10.6827ZM10.3376 23.5H13.8477V12.1667H10.3376V23.5Z"
        fill="#00343a"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  X/Twitter share badge SVG (matches Hugo exactly)                   */
/* ------------------------------------------------------------------ */
function XBadge() {
  return (
    <svg className="h-auto w-9" width="39" height="30" viewBox="0 0 39 30" fill="none">
      <rect y="0.5" width="38.2584" height="29" rx="6.44444" fill="white" />
      <rect y="0.5" width="38.2584" height="29" rx="6.44444" fill="#E6F5F0" />
      <path
        d="M10.3574 23.2581C10.4051 23.2018 10.4331 23.1676 10.4622 23.1342C12.5645 20.7314 14.667 18.3287 16.7694 15.9261C16.8577 15.8251 16.8623 15.7227 16.7831 15.6189C14.5535 12.7039 12.3241 9.78869 10.0947 6.87369C10.0672 6.83771 10.0403 6.80114 10 6.74717C10.0554 6.7446 10.0898 6.74144 10.124 6.74144C11.9738 6.74124 13.8236 6.74163 15.6734 6.73966C15.7475 6.73966 15.7926 6.76259 15.8371 6.8215C17.1201 8.52107 18.4049 10.2195 19.6896 11.9178C19.7127 11.9485 19.7374 11.9781 19.7669 12.0151C19.7985 11.9809 19.8232 11.9554 19.8467 11.9285C21.3312 10.2315 22.8159 8.53471 24.2984 6.83613C24.3575 6.76813 24.4166 6.73887 24.5079 6.73926C25.3505 6.74262 26.1931 6.74124 27.0359 6.74124H27.18C27.128 6.80351 27.0946 6.84542 27.0592 6.88575C25.0913 9.13491 23.1239 11.3845 21.1533 13.6315C21.0803 13.7147 21.0799 13.7639 21.1462 13.8513C23.4837 16.9363 25.8185 20.0237 28.1534 23.1107C28.1848 23.1522 28.2153 23.1941 28.2584 23.2524C28.205 23.255 28.1704 23.2579 28.136 23.2579C26.3356 23.2581 24.5352 23.2575 22.7348 23.2601C22.6524 23.2601 22.6044 23.2313 22.5561 23.168C21.1341 21.3053 19.7103 19.444 18.2865 17.5826C18.265 17.5543 18.2424 17.5266 18.2122 17.4885C18.18 17.5229 18.1533 17.5497 18.1284 17.5782C16.5004 19.4383 14.8722 21.2982 13.2461 23.1599C13.1844 23.2305 13.1238 23.2611 13.0283 23.2607C12.1857 23.2568 11.3431 23.2583 10.5004 23.2583C10.4624 23.2583 10.4243 23.2583 10.3572 23.2583L10.3574 23.2581ZM24.9472 21.5803C24.9051 21.5218 24.8832 21.4898 24.8598 21.4589C24.175 20.5533 23.4902 19.6479 22.8054 18.7424C20.2049 15.3039 17.6045 11.8659 15.006 8.42598C14.9518 8.35442 14.8959 8.3299 14.8089 8.3305C14.3384 8.33425 13.8677 8.33208 13.3972 8.33247C13.3634 8.33247 13.3294 8.33603 13.2738 8.339C13.3145 8.39336 13.3412 8.42954 13.3685 8.46513C14.3922 9.80351 15.4158 11.1417 16.4395 12.4799C18.7365 15.4832 21.0337 18.4866 23.3293 21.491C23.3779 21.5546 23.4277 21.5827 23.5094 21.5819C23.9437 21.5779 24.3783 21.5801 24.8126 21.5801H24.947L24.9472 21.5803Z"
        fill="#00343a"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Related Posts Grid                                                 */
/* ------------------------------------------------------------------ */
function RelatedPosts({ posts }: { posts: RelatedPost[] }) {
  if (posts.length === 0) return null;
  return (
    <div className="mx-auto max-w-screen-xl pb-24 pt-20 md:py-28">
      <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
        Explore more articles
      </h2>
      <div className="mt-6 grid gap-5 md:mt-9 md:grid-cols-3">
        {posts.map((p) => (
          <div key={p.slug} className="group relative overflow-hidden rounded-md border border-[#DCDADA] bg-white">
            {p.featured_image && (
              <div className="relative block aspect-[1200/630]">
                <img
                  src={p.featured_image}
                  // The card's link already announces the title; repeating it
                  // here would make screen readers say it twice.
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            )}
            <div className="p-6 lg:p-7">
              <h3 className="font-body font-bold text-sm leading-tight text-charcoal md:text-lg">
                {/* The link wraps the title rather than sitting beside it as a
                    bare overlay: an empty <a> passes no anchor text to the
                    target, and gives keyboard and screen-reader users nothing
                    to identify the destination by. `after:absolute` keeps the
                    whole card clickable. */}
                <Link
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="after:absolute after:inset-0 after:content-[''] group-hover:text-teal-dark"
                >
                  {p.title}
                </Link>
              </h3>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Byline                                                             */
/* ------------------------------------------------------------------ */
/**
 * The post's byline, linked to the author's page when they have one.
 *
 * Outside contributors and the collective C1 byline have no page, so they
 * render as plain text rather than a link to a 404.
 */
function Byline({ author }: { author: AuthorView }) {
  const label = `${author.name}${author.title ? `, ${author.title}` : ""}`;

  return (
    <div className="flex items-center space-x-2">
      <AuthorAvatar name={author.name} image={author.image} className="h-9 w-9 text-xs" />
      {author.hasPage ? (
        <a
          href={authorPath(author.key)}
          className="underline decoration-gray-40 underline-offset-2 transition hover:text-teal-dark hover:decoration-teal-dark"
          rel="author"
        >
          {label}
        </a>
      ) : (
        <span>{label}</span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Blog Post Component                                           */
/* ------------------------------------------------------------------ */
function BlogPost() {
  const post = Route.useLoaderData();
  const articleRef = useRef<HTMLDivElement>(null);
  const articleHtml = useMemo(() => ({ __html: post.html }), [post.html]);

  // Client-side "island": mount the interactive C1 Bridge animation into the
  // `<div data-c1-diagram="bridge">` placeholder that lives in the (otherwise
  // static, dangerouslySetInnerHTML'd) article HTML. The placeholder's
  // <noscript> fallback image is cleared once the animation takes over.
  const slug = post.slug;
  useEffect(() => {
    const el = document.querySelector<HTMLElement>('[data-c1-diagram="bridge"]');
    if (!el) return;
    el.innerHTML = "";
    return mountC1BridgeDiagram(el);
  }, [slug]);

  // Must match the canonical URL: the share targets were still pointing at the
  // pre-rebrand conductorone.com, so every social share sent readers (and the
  // resulting link equity) to the legacy domain via a redirect.
  const permalink = `${resolveSiteUrl()}/blog/${post.slug}`;

  const dateFormatted = new Date(post.date).toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  // Only surface a revision date when the post actually has one *and* it
  // differs from the publish date — "updated" on the day of publication is
  // noise, and search engines discount a modified date that tracks nothing.
  const updatedIso = post.last_updated
    ? new Date(post.last_updated).toISOString().slice(0, 10)
    : null;
  const publishedIso = new Date(post.date).toISOString().slice(0, 10);
  const showUpdated = updatedIso !== null && updatedIso !== publishedIso;
  // No weekday here — the publish date carries it as the primary timestamp;
  // the revision date is secondary and reads better short.
  const updatedFormatted = post.last_updated
    ? new Date(post.last_updated).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

  return (
    <div itemScope itemType="http://schema.org/Article">
      <div className="relative">
        {/* ============ Hero Card ============ */}
        <div className="mt-10 px-4 md:px-8">
          <div className="mx-auto max-w-[1510px]">
            <div className="relative z-30 grid items-center overflow-hidden rounded-2xl border border-[#DCDADA] bg-white/90 lg:grid-cols-2">
              {/* Left: text column */}
              <div className="p-5 lg:px-12 lg:py-10">
                {/* Breadcrumb trail. Replaces a plain "blog" eyebrow that
                    looked like a trail but linked nowhere — this one is a real
                    navigable path, and is mirrored by BreadcrumbList schema in
                    the route head. */}
                <Breadcrumbs items={blogCrumbs(post.title)} />

                {/* Title */}
                <h1
                  className="mt-4 font-display font-bold text-charcoal text-balance [font-size:var(--text-display-1)] [line-height:1.1] [letter-spacing:var(--tracking-prose-h1)]"
                  itemProp="headline name"
                >
                  {post.title}
                </h1>

                {/* Editorial lede (optional `dek:` frontmatter field) */}
                {post.dek && (
                  <p className="mt-4 max-w-[40rem] text-lg leading-snug text-gray-80 md:text-xl">
                    {post.dek}
                  </p>
                )}

                {/* Author + date */}
                <div className="mt-6 flex gap-6 text-sm">
                  {/* Wraps rather than compressing: with a revision date the row
                      carries three items, and on a narrow hero column squeezing
                      them side by side broke the byline across lines. */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {post.authors.length > 0 && <Byline author={post.authors[0]} />}
                    {/* datePublished and dateModified are separate microdata
                        properties once a post has been revised; collapsing
                        them onto one <time> asserted they were always equal. */}
                    <time className="text-gray-500" itemProp="datePublished" content={publishedIso}>
                      {dateFormatted}
                    </time>
                    {showUpdated && (
                      <time
                        className="text-gray-500"
                        itemProp="dateModified"
                        content={updatedIso}
                      >
                        Last updated {updatedFormatted}
                      </time>
                    )}
                    {!showUpdated && (
                      <meta itemProp="dateModified" content={publishedIso} />
                    )}
                  </div>
                </div>

                {/* Share buttons */}
                <div className="mt-8 flex items-center space-x-3 text-teal-dark">
                  <p className="font-body text-sm font-semibold">Share</p>
                  <ul className="flex items-center space-x-2">
                    <li>
                      <a
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(permalink)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <LinkedInBadge />
                      </a>
                    </li>
                    <li>
                      <a
                        href={`https://twitter.com/share?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(permalink)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <XBadge />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right: featured image */}
              <div className="block h-full overflow-hidden px-6 pb-8 pt-3 lg:rounded-l-xl lg:p-0">
                {post.featured_video ? (
                  <div className="relative" style={{ padding: "56.25% 0 0 0" }}>
                    <iframe
                      src={`${post.featured_video}&color=00343a&byline=0&portrait=0`}
                      className="absolute inset-0 h-full w-full"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : post.featured_image ? (
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    itemProp="image"
                    fetchPriority="high"
                    decoding="async"
                    className={`rounded-md lg:h-full lg:object-cover ${
                      {
                        left: "lg:object-left",
                        right: "lg:object-right",
                        center: "lg:object-center",
                      }[post.featured_image_position ?? "center"]
                    }`}
                  />
                ) : null}
              </div>
            </div>
          </div>

          {/* ============ Sidebar + Content ============ */}
          <div className="mx-auto mt-10 flex max-w-screen-xl flex-col md:flex-row md:gap-8 xl:gap-24">
            {/* Sidebar */}
            <div className="flex-none md:w-[17.18rem] md:pt-4">
              <div className="sticky md:top-28">
                <AskAiSummary url={`/blog/${post.slug}`} className="mb-6 hidden md:block" />
                <TableOfContents entries={post.toc} />
                <div className="mt-8 hidden md:block">
                  <BlogNewsletterSidebar />
                </div>
              </div>
            </div>

            {/* Article body */}
            <div
              ref={articleRef}
              className="content flex-1 text-gray-80"
              itemProp="articleBody"
              dangerouslySetInnerHTML={articleHtml}
            />
            <BlogAnimationEmbeds
              key={post.slug}
              contentRef={articleRef}
              html={post.html}
            />
            <BlogSceneEmbeds
              key={`scenes:${post.slug}`}
              contentRef={articleRef}
              html={post.html}
            />
            {/* Upgrades `#zoom`-flagged images in the body above to a
                full-screen view; inert on posts that don't use the flag. */}
            <ImageZoom />
          </div>

          {/* Mobile summary + newsletter (below content) */}
          <div className="mt-20 md:hidden">
            <AskAiSummary url={`/blog/${post.slug}`} className="mb-6" />
            <BlogNewsletterSidebar />
          </div>

          {/* ============ Related Posts ============ */}
          <RelatedPosts posts={post.relatedPosts} />
        </div>
      </div>
    </div>
  );
}
